// Player settings window. Game / audio / accessibility settings are saved in
// this browser; Account talks to the server; How to Play explains the game;
// Support links to the Discord and takes bug reports / suggestions.
import { useState } from "react";
import type { Me } from "@wp/shared";
import { blips } from "../../settings/audio";
import { TEXT_SCALES, type Settings } from "../../settings/settings";
import { useSettings } from "../../settings/SettingsContext";
import { resetTips } from "../../settings/tipsSeen";
import { AccountPanel } from "./AccountPanel";
import { FeedbackBox } from "./FeedbackBox";
import { HowToPlay } from "./HowToPlay";
import { Modal } from "./Modal";
import { SupportPanel } from "./SupportPanel";
import { Tabs } from "./Tabs";

export type SettingsTab = "game" | "audio" | "accessibility" | "account" | "howto" | "support";
const TABS = [
  { id: "game", label: "Game" },
  { id: "audio", label: "Audio" },
  { id: "accessibility", label: "Accessibility" },
  { id: "account", label: "Account" },
  { id: "howto", label: "How to Play" },
  { id: "support", label: "Support" },
] as const;

export interface AccountProps {
  me: Me;
  onMeChange: (me: Me) => void;
  onDeleted: () => void;
}

const GAME_TOGGLES: { key: keyof Settings["game"]; label: string }[] = [
  { key: "reduceAnimations", label: "Reduce animations" },
  { key: "reduceEffects", label: "Reduce effects (lighter 3D)" },
  { key: "showSeatBadges", label: "Show other players' name tags" },
  { key: "showTableInfo", label: "Show pot and bet info" },
  { key: "showTurnTimer", label: "Show turn timer" },
  { key: "showSpellbook", label: "Show spellbook" },
  { key: "firstTime", label: "First Time mode — show tips for new players" },
];

const VOLUMES: { key: keyof Settings["audio"]; label: string }[] = [
  { key: "master", label: "Master volume" },
  { key: "music", label: "Music volume" },
  { key: "effects", label: "Effects volume" },
];

export function SettingsWindow({
  onClose,
  account,
  initialTab = "game",
}: {
  onClose: () => void;
  account?: AccountProps;
  initialTab?: SettingsTab;
}) {
  const { settings, update } = useSettings();
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const [tipsReset, setTipsReset] = useState(false);
  const tabs = account ? TABS : TABS.filter((t) => t.id !== "account");

  return (
    <Modal title="Settings" onClose={onClose} testId="settings-window" className="wide">
      <Tabs tabs={tabs} active={tab} onChange={setTab} label="Settings sections" />
      <div role="tabpanel" className="tab-panel settings-panel">
        {tab === "game" && (
          <>
            {GAME_TOGGLES.map((t) => (
              <label key={t.key} className="toggle-row">
                <input
                  type="checkbox"
                  checked={settings.game[t.key]}
                  onChange={(e) => update({ game: { [t.key]: e.target.checked } })}
                  data-testid={`setting-${t.key}`}
                />
                <span>{t.label}</span>
              </label>
            ))}
            <div className="tips-again">
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  resetTips();
                  update({ game: { firstTime: true } });
                  setTipsReset(true);
                }}
                data-testid="tips-again"
              >
                Show tips again
              </button>
              <span className="small muted" role="status">
                {tipsReset ? "Done! The tips will show again." : "Shows all the First Time tips again, from the start."}
              </span>
            </div>
          </>
        )}
        {tab === "audio" && (
          <>
            {VOLUMES.map((v) => (
              <label key={v.key} className="slider-row">
                <span>{v.label}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(settings.audio[v.key] * 100)}
                  onChange={(e) => update({ audio: { [v.key]: Number(e.target.value) / 100 } })}
                />
                <output>{Math.round(settings.audio[v.key] * 100)}%</output>
              </label>
            ))}
            <button
              type="button"
              className="btn"
              onClick={() => {
                blips.unlock();
                blips.play("yourTurn", settings.audio);
              }}
            >
              Test sound
            </button>
          </>
        )}
        {tab === "accessibility" && (
          <>
            <div className="field">
              <span>Text size</span>
              <div className="segmented" role="radiogroup" aria-label="Text size">
                {TEXT_SCALES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={settings.accessibility.textScale === s}
                    className={settings.accessibility.textScale === s ? "seg active" : "seg"}
                    onClick={() => update({ accessibility: { textScale: s } })}
                  >
                    {s}%
                  </button>
                ))}
              </div>
            </div>
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={settings.accessibility.photosensitive}
                onChange={(e) => update({ accessibility: { photosensitive: e.target.checked } })}
              />
              <span>Photosensitivity mode (no flashing effects)</span>
            </label>
          </>
        )}
        {tab === "account" && account && <AccountPanel {...account} />}
        {tab === "howto" && <HowToPlay />}
        {tab === "support" && (
          <>
            <SupportPanel />
            <FeedbackBox signedIn={account !== undefined} />
          </>
        )}
      </div>
    </Modal>
  );
}
